import { useState, useEffect, useCallback, useRef } from "react";
import { record } from "aws-amplify/analytics/kinesis-firehose";
import { Amplify } from "aws-amplify";
import AnalyticsContext from "../../contexts/analytics/AnalyticsContext.js";
import { useTimer } from "../../hooks/useTimer";

// Constants
const ENABLE_ANALYTICS_LOGGING = false;
const SESSION_TIMEOUT = 5 * 60 * 1000; // 5 minutes
const defaultEventData = {
  attributes: {
    app_type: "cologuard_portal",
  },
  device: {
    make: window.navigator.userAgent,
    model: navigator?.userAgentData?.brands?.[0]?.brand,
    platform: {
      name: navigator?.userAgentData?.platform || window.navigator.platform,
    },
  },
};

const log = (...args) =>
  ENABLE_ANALYTICS_LOGGING && console.log("[Analytics]", ...args);

// Minimal deep merge (object-only, arrays/primitives replaced by later sources).
// Avoids adding a lodash dependency for the small set of nested record objects.
const isPlainObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value);

const merge = (target, ...sources) => {
  for (const source of sources) {
    if (!isPlainObject(source)) continue;
    for (const key of Object.keys(source)) {
      if (isPlainObject(source[key]) && isPlainObject(target[key])) {
        merge(target[key], source[key]);
      } else {
        target[key] = source[key];
      }
    }
  }
  return target;
};

// Cologuard launched_from override: a fresh entry from an email (?src=1) or
// SMS (?src=2) link is attributed to that channel. This applies ONLY to the
// entry page view, where the param is present in the URL — subsequent page
// views return null so the caller falls back to the previous page (prevUrl).
const getLaunchedFromOverride = () => {
  const src = parseInt(
    new URLSearchParams(window.location.search).get("src"),
    10
  );
  if (src === 1) return "email";
  if (src === 2) return "sms";
  return null;
};

function AnalyticsProvider({ config, children }) {
  const { identityPoolId, region, streamName } = config || {};

  // Cologuard has no auth-init event; page views fire immediately on mount.
  const [isAuthReady] = useState(true);

  // Configure amplify
  if (!identityPoolId || !region || !streamName) {
    log(
      "AnalyticsProvider missing configuration: identityPoolId, region, streamName are required"
    );
  }
  try {
    Amplify.configure({
      Auth: {
        Cognito: {
          identityPoolId,
          allowGuestAccess: true,
        },
      },
      Analytics: {
        KinesisFirehose: {
          region,
        },
      },
    });
    log("Amplify configured successfully for analytics");
  } catch (error) {
    log("Failed to configure Amplify:", error);
  }
  const { ensureSession } = useAnalyticsSession({ streamName });

  // user data management — seed uID from a previously persisted patientId so a
  // returning session carries identity on its first page view.
  const userConfig = useRef({
    uID: [localStorage.getItem("patientId") || "0"],
    role: ["0"],
  });

  const identifyUser = useCallback((userData) => {
    userConfig.current = {
      uID: userData.uID ?? ["0"],
      role: userData.role ?? ["0"],
    };
  }, []);

  const resetUser = useCallback(() => {
    userConfig.current = {
      uID: ["0"],
      role: ["0"],
    };
  }, []);

  const getUserEventData = useCallback(() => {
    const eventData = {};
    if (userConfig.current.uID) {
      eventData.endpoint = {
        User: {
          UserAttributes: {
            uID: userConfig.current.uID,
            role: userConfig.current.role,
          },
        },
      };
    }
    return eventData;
  }, []);

  // Track page views on any browser URL change. The app nests multiple
  // BrowserRouters (App + Commonlayout), so a single useLocation only sees the
  // outer router and misses the inner question-flow navigation. Hook the
  // History API directly — the same approach as the former Amplify autoTrack
  // SPA mode — so navigation from any router is captured.
  const prevUrl = useRef(document.referrer || "direct");

  // Latest-ref shim so the URL listener always calls the current closure
  // without re-subscribing on every render.
  const trackPageViewRef = useRef();
  trackPageViewRef.current = () => {
    // Ensure session is active before tracking page view
    const { currentSessionId, startTime } = ensureSession();
    try {
      const launchedFrom = getLaunchedFromOverride() ?? prevUrl.current;
      let eventData = {
        attributes: {
          url: window.location.pathname,
          launched_from: launchedFrom,
        },
        event_type: "pageView",
        session: {
          session_id: currentSessionId,
          start_timestamp: startTime,
        },
        event_timestamp: Date.now(),
      };
      merge(eventData, defaultEventData, getUserEventData());

      log(`send page view: ${window.location.pathname}`, eventData);
      record({
        streamName: streamName,
        data: eventData,
      });
      prevUrl.current = window.location.pathname;
    } catch (error) {
      console.error("Failed to record page view:", error);
    }
  };
  useEffect(() => {
    if (!isAuthReady) return undefined;

    let lastPath = null;
    const handleLocationChange = () => {
      const currentPath = window.location.pathname;
      if (currentPath === lastPath) return;
      lastPath = currentPath;
      trackPageViewRef.current();
    };

    // pushState/replaceState don't emit events; wrap them so SPA navigation
    // (from any nested router) notifies us. popstate covers back/forward.
    const wrapHistoryMethod = (method) => {
      const original = window.history[method];
      window.history[method] = function (...args) {
        const result = original.apply(this, args);
        window.dispatchEvent(new Event("analytics:locationchange"));
        return result;
      };
      return original;
    };
    const originalPushState = wrapHistoryMethod("pushState");
    const originalReplaceState = wrapHistoryMethod("replaceState");
    window.addEventListener("popstate", handleLocationChange);
    window.addEventListener("analytics:locationchange", handleLocationChange);

    // Initial page view for the URL the app loaded on.
    handleLocationChange();

    return () => {
      window.removeEventListener("popstate", handleLocationChange);
      window.removeEventListener(
        "analytics:locationchange",
        handleLocationChange
      );
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthReady]);

  /**
   * Enhanced analytics recording function
   * Automatically includes session info, device info, event timestamp and ensures session is active
   * @param {string} eventName - Name of the event to track
   * @param {object} eventData - Additional event data
   */
  const recordAnalytics = useCallback(
    (eventName, eventData = {}) => {
      try {
        // Ensure session is active
        const { currentSessionId, startTime } = ensureSession();

        // Prepare analytics data
        let analyticsData = {
          event_type: eventName,
          attributes: {
            url: window.location.pathname,
          },
          session: {
            session_id: currentSessionId,
            start_timestamp: startTime,
          },
          event_timestamp: Date.now(),
        };
        // deep merge event data objects
        merge(analyticsData, defaultEventData, eventData, getUserEventData());

        // Record the event
        record({
          streamName: streamName,
          data: analyticsData,
        });
      } catch (error) {
        console.error("Failed to record analytics event:", eventName, error);
      }
    },
    [ensureSession, streamName, getUserEventData]
  );

  // Context value
  const contextValue = {
    recordAnalytics,
    identifyUser,
    resetUser,
  };

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
}

function useAnalyticsSession({ streamName }) {
  const [sessionId, setSessionId] = useState(null);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [isSessionActive, setIsSessionActive] = useState(false);

  // End session callback
  const endSession = useCallback(() => {
    if (!isSessionActive || !sessionId) return;

    try {
      let eventData = {
        event_type: "_session.end",
        session: {
          session_id: sessionId,
          start_timestamp: sessionStartTime,
        },
        event_timestamp: Date.now(),
      };
      merge(eventData, defaultEventData);
      log(
        "end session :",
        sessionId,
        eventData.event_timestamp - eventData.session.start_timestamp,
        eventData
      );
      record({
        streamName: streamName,
        data: eventData,
      });
    } catch (error) {
      console.error("Failed to record session end:", error);
    }

    setIsSessionActive(false);
    setSessionId(null);
    setSessionStartTime(null);
  }, [isSessionActive, sessionId, sessionStartTime, streamName]);

  // Session timeout timer
  const sessionTimer = useTimer(endSession, SESSION_TIMEOUT);

  const ensureSession = useCallback(() => {
    if (isSessionActive) {
      // Reset timer if session is already active
      sessionTimer.reset();
      return { currentSessionId: sessionId, startTime: sessionStartTime };
    }
    const newSessionId = crypto.randomUUID();
    const startTime = Date.now();

    setSessionId(newSessionId);
    setSessionStartTime(startTime);
    setIsSessionActive(true);
    try {
      let eventData = {
        event_type: "_session.start",
        session: {
          session_id: newSessionId,
          start_timestamp: startTime,
        },
        attributes: {
          url: window.location.pathname,
        },
        event_timestamp: Date.now(),
      };
      merge(eventData, defaultEventData);
      log(`starting now session: ${newSessionId} ${startTime}`, eventData);
      record({
        streamName: streamName,
        data: eventData,
      });
    } catch (error) {
      console.error("Failed to record session start:", error);
    }

    // Start session timeout timer
    sessionTimer.start();

    return { currentSessionId: newSessionId, startTime };
  }, [isSessionActive, sessionId, sessionTimer, streamName, sessionStartTime]);

  return {
    ensureSession,
    endSession,
  };
}

export default AnalyticsProvider;
